import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import ReactModal from "react-modal";
import { ScrollToTop } from "../utils.js";
import { SearchBar } from "../components/searchbar.js";
import { NavDropdown } from "../components/navmenus/navdropdown.js";
import { RegionMegaMenu } from "../components/navmenus/regionmegamenu.js";
import { AboutMenu } from "../components/navmenus/aboutmenu.js";
import { MobileNav } from "../components/navmenus/mobilenav.js";
import helioDataLogoCropped from "../images/heliodata-logo-cropped-3.png";
import nasaLogo from "../images/nasa-logo.png";

const MOBILE_NAV_BREAKPOINT = 1200;

/**
 * Display header of the website.
 *
 * @param scrollToTop In most cases, don't set this. Normally, this
 *   element includes an action to scroll the user to the top of the page.
 *   This setting disables that.
 * @param wide Make the header wide and expand to the width of the screen.
 *   Default is true.
 * @return JSX element
 */
export function Header(props) {
  let { scrollToTop, hideSearch } = props;

  if (scrollToTop === undefined) {
    scrollToTop = true;
  }

  let wide = true;

  if (props.wide !== undefined) {
    wide = props.wide;
  }

  const [currentQuery, setCurrentQuery] = useState("");
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_NAV_BREAKPOINT
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const navRef = useRef(null);

  // Click toggles a dropdown: the same id closes it, a different id swaps the
  // open one. Only one desktop dropdown is open at a time.
  const toggleMenu = useCallback((id) => {
    setOpenMenu((prev) => (prev === id ? null : id));
  }, []);
  const closeMenu = useCallback(() => setOpenMenu(null), []);

  const BANNER_ENABLED = false; // Turn on and off the banner here
  const [hideInfoAlert, setHideInfoAlert] = useState(
    () => !BANNER_ENABLED || sessionStorage.getItem('spdfAlertDismissed') === 'true'
  );
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const closeAlert = () => {
    sessionStorage.setItem('spdfAlertDismissed', 'true');
    setHideInfoAlert(true);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_NAV_BREAKPOINT);
    };

    // Use the composed path captured at dispatch time so a click on an
    // element that re-renders away (e.g. a mobile drill-down row) is still
    // recognized as inside the menu.
    const handleClickOutside = (event) => {
      const path =
        typeof event.composedPath === "function" ? event.composedPath() : [];
      const clickedInside = (ref) =>
        ref.current &&
        (path.length
          ? path.includes(ref.current)
          : ref.current.contains(event.target));
      if (menuRef.current && !clickedInside(menuRef)) setMenuOpen(false);
      if (navRef.current && !clickedInside(navRef)) setOpenMenu(null);
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("click", handleClickOutside);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Reset open-menu state when crossing the mobile/desktop breakpoint so a
  // dropdown left open before resizing doesn't reappear after flipping back.
  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
  }, [isMobile]);


  return (
    <div className="header-bar-background">
      {!hideInfoAlert &&
        <div className={`alertContainer`}>
          <div className="alert">
            <div className="alertTextContainer">

            </div>
            <div className="alertText">Important Information Goes Here.{" "}<button className="alertLearnMore" onClick={() => setShowLearnMoreModal(true)}>Learn More</button></div>
            <button className="alertClose" onClick={closeAlert}>&times;</button>
          </div>
        </div>}
      <div className={wide ? "header-bar-wide" : "header-bar"}>
        {scrollToTop ? <ScrollToTop /> : ""}
        <div>
          <div className="header-logos">
            <Link to="https://www.nasa.gov/">
              <img src={nasaLogo} alt="NASA" className="header-logo" />
            </Link>
            <Link to="/">
              <img src={helioDataLogoCropped} alt="HelioData" className="header-logo-img" />
            </Link>
          </div>
        </div>
        <div className="header-button-button-list">
          <div className="header-right-side-items">
            {!isMobile && !hideSearch &&
              <span className="header-search-item">
                <SearchBar
                  currentQuery={currentQuery}
                  setCurrentQuery={setCurrentQuery}
                  navigate={navigate}
                  noControls={true}
                  whiteBg={false}
                  shortLength={true}
                  placeholder={"Search the catalog..."}
                />
              </span>}


            {isMobile ? (
              <div className="header-hamburger-menu" ref={menuRef}>
                <button
                  className={menuOpen ? "header-hamburger-light-button" : "header-hamburger-button"}
                  aria-label="Open navigation menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  &#9776;
                </button>
                {menuOpen && (
                  <MobileNav
                    onClose={() => setMenuOpen(false)}
                    SearchSlot={
                      !hideSearch && (
                        <SearchBar
                          currentQuery={currentQuery}
                          setCurrentQuery={setCurrentQuery}
                          navigate={navigate}
                          noControls={true}
                          whiteBg={false}
                          shortLength={true}
                          placeholder={"Search the catalog..."}
                        />
                      )
                    }
                  />
                )}
              </div>
            )
              :
              (
                <div className="header-right-bar-buttons" ref={navRef}>
                  <span className="header-bar-button">
                    <NavDropdown
                      label="Data by Region"
                      menuId="region"
                      isOpen={openMenu === "region"}
                      onToggle={toggleMenu}
                      onClose={closeMenu}
                    >
                      <RegionMegaMenu onNavigate={closeMenu} />
                    </NavDropdown>
                  </span>

                  <span className="header-bar-button">
                    <Link to="/missionlist">Missions & Observatories</Link>
                  </span>

                  <span className="header-bar-button">
                    <NavDropdown
                      label="About Us"
                      menuId="about"
                      isOpen={openMenu === "about"}
                      onToggle={toggleMenu}
                      onClose={closeMenu}
                    >
                      <AboutMenu onNavigate={closeMenu} />
                    </NavDropdown>
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>
      <ReactModal
        isOpen={showLearnMoreModal}
        onRequestClose={() => setShowLearnMoreModal(false)}
        className="alertModalContent"
        overlayClassName="alertModalOverlay"
      >
        <div className="alertModalBody">
          <h2>Important Information Goes Here</h2>
          <p>
            If there is more information about this alert, put it here.
          </p>
          <p>
            We apologize for the inconvenience. Services are expected to be restored once the
            server relocation is complete. Please check back for updates.
          </p>
          <button className="alertModalClose" onClick={() => setShowLearnMoreModal(false)}>Close</button>
        </div>
      </ReactModal>
    </div>
  );
}
